import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Container,
  Paper,
  CircularProgress,
  Alert,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { request } from '../../Utils/request';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';

interface HelpCenterItem {
  _id: string;
  title: string;
  description: string;
  status: string;
  priority: number;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

interface HelpCenterApiResponse {
  success: boolean;
  data: HelpCenterItem[];
}

interface HelpCenterData {
  aboutUs: {
    title: string;
    description: string;
    logo?: string;
    tagline?: string;
  };
  helpCenter: {
    title: string;
    items: HelpCenterItem[];
  };
  contactUs: {
    title: string;
    email: string;
    phone: string;
    support: string;
    location: string;
    facebook: string;
    instagram: string;
  };
}

interface AppConfigData {
  appName: string;
  email: string;
  version: string;
  phone: string;
  companyAddress: string;
  facebook: string;
  instagram: string;
  youtube: string;
}

const HelpCenter: React.FC = () => {
  const navigate = useNavigate();
  const [helpCenterItems, setHelpCenterItems] = useState<HelpCenterItem[]>([]);
  const [appConfig, setAppConfig] = useState<AppConfigData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch help center data
        const helpCenterResponse = await request<HelpCenterApiResponse>('/profile/help-center', {
          authorization: true,
        });
        
        // Fetch app config data
        const appConfigResponse = await request<{ data: AppConfigData }>('/profile/app-config', {
          authorization: true,
        });

        setHelpCenterItems(helpCenterResponse.data);
        setAppConfig(appConfigResponse.data);
      } catch (err) {
        console.error('Error fetching help center data:', err);
        setError('Failed to load help center data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  const handleHelpItemClick = (item: HelpCenterItem) => {
    const newExpandedItems = new Set(expandedItems);
    if (newExpandedItems.has(item._id)) {
      newExpandedItems.delete(item._id);
    } else {
      newExpandedItems.add(item._id);
    }
    setExpandedItems(newExpandedItems);
  };

  const handleContactClick = (type: string, value: string) => {
    if (!value || value === 'Facebook' || value === 'Instagram' || value === 'Youtube') {
      console.log('No URL provided for', type);
      return;
    }
    
    switch (type) {
      case 'email':
        window.location.href = `mailto:${value}`;
        break;
      case 'phone':
        window.location.href = `tel:${value}`;
        break;
      case 'facebook':
        window.open(value, '_blank', 'noopener,noreferrer');
        break;
      case 'instagram':
        window.open(value, '_blank', 'noopener,noreferrer');
        break;
      case 'youtube':
        window.open(value, '_blank', 'noopener,noreferrer');
        break;
      default:
        console.log('Contact clicked:', type, value);
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        bgcolor: 'white', 
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        bgcolor: 'white', 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <TopNavBar title='Help Center' canGoBack />
        <Container maxWidth="sm" sx={{ flex: 1, py: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      bgcolor: 'white', 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <TopNavBar title='Help Center' canGoBack />

      <Container maxWidth="sm" sx={{ flex: 1, py: 2 }}>
        <Box>
          
          {/* About Us Section */}
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#000' }}>
              About us
            </Typography>
            
            {/* Logo and Tagline */}
            <Box sx={{ mb: 2, textAlign: 'left' }}>
              <Box
                component="img"
                src="/assets/images/ifinds%20Logo_Rev-2.png"
                alt="iFINDS Logo"
                onError={(e) => {
                  console.error('Image failed to load:', e);
                  console.log('Trying alternative path...');
                }}
                onLoad={() => {
                  console.log('Image loaded successfully');
                }}
                sx={{
                  height: 80,
                  width: 'auto',
                  maxWidth: '100%',
                  mb: 1,
                  objectFit: 'contain'
                }}
              />
            </Box>

            <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6 }}>
              Welcome to ifinds, the ultimate destination for discovering local experiences! Whether you're a local looking for hidden gems in your own backyard, or a traveler seeking authentic experiences in a new destination, ifinds has got you covered. Our platform is designed to help you easily explore and book unique experiences that showcase the best of what your city or town has to offer. From food tours and outdoor adventures to cultural events and off-the-beaten-path attractions, our curated selection of experiences will help you make the most of your time and create unforgettable memories. So what are you waiting for? Let ifinds be your guide to discovering the best of your local community.
            </Typography>
          </Box>

          <Divider />

          {/* Help Center Section */}
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#000' }}>
              Help center
            </Typography>
            
            <List sx={{ p: 0 }}>
              {helpCenterItems?.map((item, index) => (
                <React.Fragment key={item._id}>
                  <ListItem disablePadding>
                    <ListItemButton 
                      onClick={() => handleHelpItemClick(item)}
                      sx={{ 
                        py: 1.5,
                        '&:hover': {
                          backgroundColor: '#F5F5F5'
                        }
                      }}
                    >
                      <ListItemText 
                        primary={item.title}
                        sx={{ 
                          '& .MuiListItemText-primary': {
                            color: '#49454F',
                            fontWeight: 500
                          }
                        }}
                      />
                      <IconButton 
                        size="small"
                        sx={{ 
                          color: '#9E9E9E',
                          transform: expandedItems.has(item._id) ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease-in-out'
                        }}
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    </ListItemButton>
                  </ListItem>
                  
                  {/* Expandable Content */}
                  <Collapse in={expandedItems.has(item._id)} timeout="auto" unmountOnExit>
                    <Box sx={{ 
                      px: 3, 
                      pb: 2,
                      bgcolor: '#FAFAFA',
                      borderLeft: '3px solid #008387'
                    }}>
                      <Typography variant="body2" sx={{ 
                        color: '#666',
                        lineHeight: 1.6,
                        pt: 1
                      }}>
                        {item.description}
                      </Typography>
                    </Box>
                  </Collapse>
                  
                  {index < (helpCenterItems?.length || 0) - 1 && (
                    <Divider sx={{ mx: 2 }} />
                  )}
                </React.Fragment>
              ))}
            </List>
          </Box>

          <Divider />

          {/* Contact Us Section */}
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#000' }}>
              Contact Us
            </Typography>
            
            <List sx={{ p: 0 }}>
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => handleContactClick('email', appConfig?.email || 'ifinds@bilvacrop.in')}
                  sx={{ 
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: '#F5F5F5'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <EmailIcon sx={{ color: '#008387' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={appConfig?.email || 'ifinds@bilvacrop.in'}
                    sx={{ 
                      '& .MuiListItemText-primary': {
                        color: '#49454F',
                        fontWeight: 500
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>
              <Divider sx={{ mx: 2 }} />
              
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => handleContactClick('phone', appConfig?.phone || '9790255717')}
                  sx={{ 
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: '#F5F5F5'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <PhoneIcon sx={{ color: '#008387' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={appConfig?.phone || '9790255717'}
                    sx={{ 
                      '& .MuiListItemText-primary': {
                        color: '#49454F',
                        fontWeight: 500
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>
              <Divider sx={{ mx: 2 }} />
              
              {/* <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => handleContactClick('phone', 'Support')}
                  sx={{ 
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: '#F5F5F5'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <PhoneIcon sx={{ color: '#008387' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Support"
                    sx={{ 
                      '& .MuiListItemText-primary': {
                        color: '#49454F',
                        fontWeight: 500
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>
              <Divider sx={{ mx: 2 }} /> */}
              
              {/* <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => handleContactClick('location', appConfig?.companyAddress || 'Vayalur')}
                  sx={{ 
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: '#F5F5F5'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <LocationIcon sx={{ color: '#008387' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={appConfig?.companyAddress || 'Vayalur'}
                    sx={{ 
                      '& .MuiListItemText-primary': {
                        color: '#49454F',
                        fontWeight: 500
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>
              <Divider sx={{ mx: 2 }} /> */}
              
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => handleContactClick('facebook', appConfig?.facebook || '')}
                  disabled={!appConfig?.facebook}
                  sx={{ 
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: '#F5F5F5'
                    },
                    '&:disabled': {
                      opacity: 0.6
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <FacebookIcon sx={{ color: '#008387' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={'Facebook'}
                    sx={{ 
                      '& .MuiListItemText-primary': {
                        color: '#49454F',
                        fontWeight: 500
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>
              <Divider sx={{ mx: 2 }} />
              
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => handleContactClick('instagram', appConfig?.instagram || '')}
                  disabled={!appConfig?.instagram}
                  sx={{ 
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: '#F5F5F5'
                    },
                    '&:disabled': {
                      opacity: 0.6
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <InstagramIcon sx={{ color: '#008387' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={'Instagram'}
                    sx={{ 
                      '& .MuiListItemText-primary': {
                        color: '#49454F',
                        fontWeight: 500
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => handleContactClick('youtube', appConfig?.youtube || '')}
                  disabled={!appConfig?.youtube}
                  sx={{ 
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: '#F5F5F5'
                    },
                    '&:disabled': {
                      opacity: 0.6
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <YouTubeIcon sx={{ color: '#008387' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={'Youtube'}
                    sx={{ 
                      '& .MuiListItemText-primary': {
                        color: '#49454F',
                        fontWeight: 500
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </List>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default HelpCenter;
